import { Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useState } from "react";
import EntityConstants from "../../configs/EntityConstants";
import { GET, POST } from "../../service/CRUDService";
import { emailRegex } from "../../configs/App.Regex";
import Toast from "../Toast/Toast";
import { TbSend } from "react-icons/tb";
import { IoCloseCircleOutline } from "react-icons/io5";
import EmailIcon from "../../resources/icons/icons8-email-48.svg";
import CfLoader from "../CfLoader/CfLoader";
import WarningIcon from "../../resources/icons/warning-info.svg";

const NotificationModal = ({
  ImageUrl,
  Recipient_name,
  TriggerBtn,
  TriggerBtnToggle,
  Credential_id,
}) => {
  const [emailArray, setEmailArray] = useState([]);
  const [emailError, setEmailError] = useState("");
  const [currentEmail, setCurrentEmail] = useState("");
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [fromEmail, setFromEmail] = useState("");
  const [defaultEmail, setDefaultEmail] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  const getSentFromEmail = async () => {
    try {
      const response = await GET(`${EntityConstants.GET_EMAIL_DETAILS}`);
      const data = await response.json();
      setFromEmail(data.email);
      setDefaultEmail(data.default_email);
      setEmailMessage(data.message);
      setIsEmailVerified(data.is_verified);
    } catch (error) {
      console.error(error);
    }
  };

  const handlePasteEmails = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text");

    // Use regex to match valid email addresses
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;

    // Extract valid emails
    const emailMatches = pastedText.match(emailRegex) || [];

    // Remove duplicates and filter empty segments
    const uniqueEmails = [
      ...new Set(emailMatches.map((email) => email.trim())),
    ];

    // Validate and add to the emailArray
    uniqueEmails.forEach((email) => {
      if (emailArray.includes(email)) {
        setEmailError(`* Email "${email}" Already Exists`);
      } else {
        setEmailArray((prev) => [...prev, email]);
      }
    });

    // Check for invalid emails (anything that isn't in the matches)
    const invalidEmails = pastedText
      .split(/[,\s]+/) // Split on commas and whitespace
      .map((email) => email.trim())
      .filter(
        (email) =>
          email && !emailRegex.test(email) && !uniqueEmails.includes(email)
      );

    if (invalidEmails.length > 0) {
      setEmailError(
        `The following emails are invalid: ${invalidEmails.join(", ")}`
      );
    } else {
      setEmailError(""); // Clear error if no invalid emails
    }

    // Clear the input
    setCurrentEmail("");
  };

  const handleSendNotificationEmail = (credential_id, emailArray) => {
    const body = {
      notification_type: "email",
      credential_id: credential_id,
      target: emailArray,
    };
    POST(`${EntityConstants.SEND_NOTIFICATION}`, body)
      .then((resp) => resp.json())
      .then((data) => {
        if (data.error) {
          return Toast({
            type: "error",
            title: "Error",
            message: data.message,
          });
        } else {
          return Toast({
            type: "success",
            title: "Success",
            message: data.message,
          });
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleAddEmail = () => {
    if (!emailError) {
      setEmailArray((prev) => [...prev, currentEmail]);
      setCurrentEmail("");
    }
  };

  const handleSendNotification = (credential_id, emailList) => {
    if (emailList.length > 0) {
      handleSendNotificationEmail(credential_id, emailList);
    } else {
      setEmailError("Add emails to send notification");
    }
  };

  const getDefaultEmail = (Credential_id) => {
    GET(`${EntityConstants.GET_DEFAULT_EMAIL}${Credential_id}`)
      .then((resp) => resp.json())
      .then((data) => {
        data.email && setEmailArray(data.email);
      })
      .catch((error) => console.error(error));
  };

  useEffect(() => {
    if (currentEmail && emailRegex.test(currentEmail)) {
      if (emailArray.includes(currentEmail)) {
        setEmailError("* Email Already Exists");
      } else {
        setEmailError("");
      }
    } else if (!currentEmail) {
      setEmailError("");
    } else {
      setEmailError("* Please Enter Valid Email");
    }
  }, [currentEmail, emailArray]);

  useEffect(() => {
    Credential_id && getDefaultEmail(Credential_id);
  }, [Credential_id]);

  useEffect(() => {
    getSentFromEmail();
  }, []);

  return (
    <Transition
      appear
      show={Credential_id === TriggerBtn}
      enter="transition-opacity duration-75"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-150"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      className="relative z-10"
      as="div"
    >
      <div
        className="fixed inset-0 bg-black bg-opacity-25 cursor-pointer "
        role="button"
        tabIndex={0}
        onClick={() => TriggerBtnToggle(null)}
        onKeyDown={(e) =>
          (e.key === "Enter" || e.key === " ") && TriggerBtnToggle(null)
        }
      />

      <div className="fixed inset-0 overflow-y-auto w-full lg:w-10/12 xl:w-8/12 p-3 md:p-0  mx-auto my-auto h-fit">
        <div className="flex min-h-full items-center justify-center py-2 text-center h-full">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <div className="flex flex-col sm:flex-row transform rounded-2xl bg-cfSecondary text-left align-shadow-xl transition-all w-full min-h-[30rem] max-h-[50rem]">
              <div className="bg-cover bg-[#3538cd]/90 p-4 w-full sm:w-1/2 stretch rounded-t-2xl sm:rounded-l-2xl sm:rounded-tr-none  flex flex-col justify-center items-center">
                <div className="rounded-md w-full h-fit drop-shadow-lg flex flex-col justify-center items-center">
                  <img
                    src={`${ImageUrl}`}
                    alt=""
                    onLoad={() => setIsImageLoading(false)}
                    className={` ${
                      isImageLoading ? "hidden" : "block"
                    } rounded-md w-full my-auto max-h-[40rem]`}
                  />
                  {isImageLoading && (
                    <div className="rounded-md bg-white w-full h-full flex justify-center items-center">
                      <div className="rounded-md w-full h-96 bg-black/5 flex justify-center items-center">
                        <CfLoader />
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col gap-2 items-center p-2 text-white">
                    <h1 className="font-semibold text-lg capitalize">
                      {Recipient_name}
                    </h1>
                    <h2>{Credential_id}</h2>
                  </div>
                </div>
              </div>
              <div className="w-full sm:w-1/2 p-6">
                <div className="flex flex-col gap-2 items-baseline justify-between font-medium leading-6 text-gray-900 mb-3">
                  <h1 className="text-2xl text-cfGlobalText">
                    Resend Notification
                  </h1>
                </div>
                <div className="flex flex-col gap-4">
                  {(fromEmail || defaultEmail) && (
                    <>
                      <div
                        className={`text-cfGlobalText text-base w-full text-left flex`}
                      >
                        <p>From:</p>&nbsp;&nbsp;
                        <p>
                          {isEmailVerified === false ? defaultEmail : fromEmail}
                        </p>
                      </div>
                      {isEmailVerified === false && (
                        <span className="flex">
                          <div className="flex w-full text-start justify-start items-center gap-2 p-2 bg-cfEmailWarningContainerBg rounded-lg">
                            <img
                              alt="Warning"
                              src={WarningIcon}
                              height={35}
                              width={35}
                            />
                            <div className="text-cfEmailWarningTextBg text-sm">
                              {emailMessage}
                            </div>
                          </div>
                        </span>
                      )}
                    </>
                  )}
                  <div className="flex gap-2 items-center">
                    <div className="flex border-[1.5px]  border-cfGlobalText rounded-md w-full">
                      <p className="text-cfGlobalText p-1 flex items-center justify-center">
                        <img src={EmailIcon} alt="" width={30} />
                      </p>
                      <div
                        name="emailForm"
                        className="flex items-start gap-2 flex-wrap border-l-[1.5px] border-cfGlobalText p-2 bg-cfSecondary rounded-r-[0.25rem] h-40 overflow-y-auto"
                      >
                        {emailArray.map((email) => (
                          <p
                            key={email}
                            className="bg-[#3538cd] hover:bg-[#3538cd]/90 text-cfSecondary px-2 py-1 rounded-full flex gap-1 items-center"
                          >
                            {email}
                            <button
                              type="button"
                              className="flex items-center justify-center rounded-full text-lg h-6 w-6 cursor-pointer"
                              onClick={() =>
                                setEmailArray(
                                  emailArray.filter((item) => item !== email)
                                )
                              }
                            >
                              <IoCloseCircleOutline size={25} />
                            </button>
                          </p>
                        ))}
                        <div className="w-fit flex gap-2 items-center">
                          {
                            <input
                              type="email"
                              name="email"
                              id="email"
                              placeholder="john.doe@gmail.com"
                              value={currentEmail}
                              size={currentEmail.length}
                              style={{
                                borderWidth: "2px",
                                borderRadius: "99999px",
                              }}
                              className={`border-2 border-cfPrimaryLight focus:border-cfPrimaryCardHover ${
                                currentEmail
                                  ? "rounded-full"
                                  : "border-none rounded-lg"
                              } focus:outline-none w-full text-cfGlobalText placeholder:text-gray-400 px-2 py-1`}
                              onChange={(e) => setCurrentEmail(e.target.value)}
                              onKeyDown={(e) =>
                                e.key === "Enter" && handleAddEmail()
                              }
                              onPaste={handlePasteEmails}
                            />
                          }
                          {!emailError && currentEmail && (
                            <button
                              className="rounded-full text-lg bg-cfPrimaryLight hover:bg-cfPrimaryCardHover text-cfSecondary flex items-center justify-center px-2 py-1"
                              onClick={handleAddEmail}
                            >
                              +Add
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  {emailError && emailArray.length > 0 && (
                    <p
                      className={`${
                        emailError.startsWith("*")
                          ? "text-red-500"
                          : "text-[#3538cd]"
                      }`}
                    >
                      {emailError}
                    </p>
                  )}
                </div>
                <div className="flex justify-end py-2">
                  <button
                    className="bg-[#3538cd] hover:bg-cfPrimaryCardHover disabled:bg-cfDisabled text-cfSecondary rounded-lg px-4 py-2 flex gap-2 items-center text-lg font-semibold"
                    disabled={!(emailArray.length > 0)}
                    onClick={() =>
                      handleSendNotification(Credential_id, emailArray)
                    }
                  >
                    <TbSend /> Send
                  </button>
                </div>
              </div>
            </div>
          </Transition.Child>
        </div>
      </div>
    </Transition>
  );
};

export default NotificationModal;
